import { assign, createMachine, raise, sendTo, spawn } from 'xstate';
import { pure } from 'xstate/lib/actions';
import { PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../const/analytics';
import { paymentTypes } from '../const/paymentOptions';
import { getMaximumPaymentDays, isSecuredInstallmentLoanProduct } from '../helpers';
import { getOneTimePaymentEligibilityDetail } from '../helpers/parseEligibility';
import shouldEnableManagePaymentMethods from '../helpers/shouldEnableManagePaymentMethods';
import apiMachine from './apiMachine/apiMachine';
import autopayMachine from './autopay/autopayMachine';
import autopayBannerMachine from './autopayBannerMachine';
import oneTimePaymentMachine from './oneTimePayment/oneTimePaymentMachine';
import addPaymentMethodMachine from './paymentMethod/addPaymentMethodMachine';
import paymentMethodMachine from './paymentMethod/paymentMethodMachine';
import payoffPaymentMachine from './payoffPayment/payoffPaymentMachine';
import pendingPaymentMachine from './pendingPayment/pendingPaymentMachine';
const PAYMENT_FLOW_ID = 'paymentFlow';
const mainMachine = createMachine({
    predictableActionArguments: true,
    preserveActionOrder: true,
    id: 'mainMachine',
    context: {
        apiConfig: undefined,
        apiMachineRef: undefined,
        currentPaymentFlow: [null, null],
        featureFlags: undefined,
        helpNumber: undefined,
        triggerAnalyticsEvent: undefined
    },
    schema: {
        events: {}
    },
    initial: 'open',
    states: {
        closed: {
            id: 'closed',
            type: 'final',
            entry: ({ conversionCount, handleClose, handleCloseOnConversion }) => {
                if (handleCloseOnConversion && conversionCount > 0) {
                    handleCloseOnConversion();
                }
                if (handleClose) {
                    handleClose();
                }
            }
        },
        open: {
            id: 'open',
            initial: 'initializing',
            states: {
                initializing: {
                    id: 'initializing',
                    initial: 'fetchApiData',
                    states: {
                        fetchApiData: {
                            id: 'fetchApiData',
                            initial: 'fetchAccountSummary',
                            states: {
                                fetchAccountSummary: {
                                    entry: [
                                        'spawnApiMachine',
                                        sendTo('apiMachine', { type: 'FETCH_ACCOUNT_SUMMARY' })
                                    ],
                                    on: {
                                        AFTER_SUMMARY_FETCH: [
                                            {
                                                cond: 'isDelinquentAccount',
                                                target: '#open.paymentNotAllowed.delinquentAccount'
                                            },
                                            { target: 'fetchPaymentEligibility' }
                                        ]
                                    }
                                },
                                fetchPaymentEligibility: {
                                    entry: [
                                        sendTo('apiMachine', {
                                            type: 'FETCH_PAYMENT_ELIGIBILITY'
                                        })
                                    ],
                                    on: {
                                        AFTER_ELIGIBILITY_FETCH: [
                                            { target: 'fetchRemainingApis' }
                                        ]
                                    }
                                },
                                fetchRemainingApis: {
                                    entry: [
                                        'shouldInvokeAccountActivity',
                                        sendTo('apiMachine', { type: 'FETCH_AUTOPAY_STATUS' }),
                                        sendTo('apiMachine', {
                                            type: 'FETCH_ALTERNATE_PAYMENT_METHODS'
                                        }),
                                        sendTo('apiMachine', { type: 'FETCH_PAYMENT_METHODS' })
                                    ],
                                    on: {
                                        AFTER_ACTIVITY_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        },
                                        AFTER_AUTOPAY_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        },
                                        AFTER_PAYMENT_METHODS_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        },
                                        AFTER_ALTERNATE_PAYMENT_METHODS_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        }
                                    }
                                }
                            }
                        }
                    },
                    on: {
                        START_PAYMENT_FLOW: [
                            {
                                target: 'paymentNotAllowed.oneTimePaymentNotEligible',
                                cond: 'isOneTimePaymentNotEligible',
                                actions: [
                                    'spawnPendingPaymentMachine',
                                    'spawnAutopayBannerMachine'
                                ]
                            },
                            {
                                target: 'paymentFlow',
                                actions: [
                                    'spawnOneTimePaymentMachine',
                                    'spawnAutopayBannerMachine',
                                    {
                                        type: 'triggerAnalyticsEvent',
                                        eventOptions: {
                                            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                                            pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT,
                                            pageName: PAGE_NAME.START
                                        }
                                    }
                                ]
                            }
                        ],
                        API_ERROR: {
                            target: 'error',
                            // eslint-disable-next-line no-console
                            actions: (context, event) => console.error('API_ERROR', event)
                        }
                    }
                },
                managePaymentMethod: {
                    id: 'managePaymentMethod',
                    entry: assign({
                        paymentMethodActorRef: ({ apiMachineRef, featureFlags }) => {
                            const { accountSummaryQueryResult } = apiMachineRef.getSnapshot().context;
                            const { product } = accountSummaryQueryResult.data;
                            if (shouldEnableManagePaymentMethods(product.type, featureFlags.isManagePaymentMethodsEnabled)) {
                                return spawn(paymentMethodMachine.withContext({
                                    apiMachineRef
                                }), { name: 'managePaymentMethod' });
                            }
                            return spawn(addPaymentMethodMachine.withContext({
                                apiMachineRef
                            }), { name: 'addPaymentMethod' });
                        }
                    }),
                    exit: assign({
                        paymentMethodActorRef: ({ paymentMethodActorRef }) => {
                            paymentMethodActorRef.stop();
                            return undefined;
                        }
                    })
                },
                paymentFlow: {
                    id: PAYMENT_FLOW_ID,
                    on: {
                        INCREMENT_CONVERSION: {
                            actions: [
                                assign({
                                    conversionCount: ({ conversionCount }) => conversionCount + 1
                                }),
                                /*
                                 * autopay banner needs to know when an autopay enrollment/unenrollment
                                 * may have happened and refresh the state accordingly
                                 */
                                'sendToAutopayBanner'
                            ]
                        }
                    }
                },
                paymentNotAllowed: {
                    id: 'paymentNotAllowed',
                    states: {
                        delinquentAccount: {
                            // This state doesn't have anything to make a further transition. So, it will not have any event.
                            id: 'delinquentAccount'
                        },
                        oneTimePaymentNotEligible: {
                            id: 'oneTimePaymentNotEligible'
                        }
                    }
                },
                error: {}
            },
            on: {
                HOE_UPDATE_BANNER_VISIBILITY: {
                    actions: 'evaluateBannerVisibility'
                },
                CANCEL: '#mainMachine.closed',
                SET_ONE_TIME_PAYMENT_FLOW: {
                    actions: ['spawnOneTimePaymentMachine', 'sendToAutopayBanner'],
                    target: `open.${PAYMENT_FLOW_ID}`
                },
                SET_AUTOPAY_FLOW: {
                    actions: ['spawnAutopayMachine', 'sendToAutopayBanner'],
                    target: `open.${PAYMENT_FLOW_ID}`
                },
                SET_PAYOFF_PAYMENT_FLOW: {
                    actions: ['spawnPayoffPaymentMachine', 'sendToAutopayBanner'],
                    target: `open.${PAYMENT_FLOW_ID}`
                },
                GO_TO_PAYMENT_METHOD_FLOW: {
                    target: 'open.managePaymentMethod'
                },
                GO_TO_PAYMENT_FLOW: {
                    target: `open.${PAYMENT_FLOW_ID}`
                }
            }
        }
    }
}, {
    actions: {
        evaluateBannerVisibility: sendTo('autopayBanner', {
            type: 'UPDATE_BANNER_VISIBILITY'
        }),
        sendToAutopayBanner: sendTo('autopayBanner', (context, event) => {
            return Object.assign(Object.assign({}, event), { mainMachineContext: context });
        }),
        shouldInvokeAccountActivity: pure((context) => {
            const { accountSummaryQueryResult } = context.apiMachineRef.getSnapshot().context;
            const productType = accountSummaryQueryResult.data.product.type;
            if (isSecuredInstallmentLoanProduct(productType))
                return undefined;
            return sendTo('apiMachine', () => {
                return {
                    type: 'FETCH_ACCOUNT_ACTIVITY'
                };
            });
        }),
        spawnApiMachine: assign({
            apiMachineRef: ({ apiConfig, apiMachineRef, featureFlags, helpNumber, queryClient }) => {
                if (apiMachineRef) {
                    return apiMachineRef;
                }
                const queryResult = {
                    data: undefined,
                    error: undefined,
                    isError: false
                };
                return spawn(apiMachine.withContext({
                    accountActivityQueryResult: queryResult,
                    accountSummaryQueryResult: queryResult,
                    alternatePaymentMethodsQueryResult: queryResult,
                    apiConfig,
                    autopayStatusQueryResult: queryResult,
                    bankNameQueryResult: queryResult,
                    featureFlags,
                    helpNumber,
                    paymentEnterpriseEligibilityQueryResult: queryResult,
                    paymentMethodsQueryResult: queryResult,
                    queryClient
                }), { name: 'apiMachine' });
            }
        }),
        spawnAutopayBannerMachine: assign({
            autopayBannerRef: (context) => {
                const { autopayBannerRef, currentPaymentFlow, apiMachineRef, featureFlags } = context;
                if (autopayBannerRef) {
                    autopayBannerRef.stop();
                }
                return spawn(autopayBannerMachine.withContext({
                    apiMachineRef,
                    currentPaymentFlow,
                    featureFlags
                }), {
                    name: 'autopayBanner'
                });
            }
        }),
        spawnOneTimePaymentMachine: assign({
            currentPaymentFlow: (context) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const { apiMachineRef, featureFlags, triggerAnalyticsEvent } = context;
                const oneTimeRef = spawn(oneTimePaymentMachine.withContext({
                    apiMachineRef,
                    featureFlags,
                    shouldEnrollInOneClickAutopay: false,
                    shouldShowOneClickAutopay: false,
                    triggerAnalyticsEvent
                }));
                return [paymentTypes.ONCE, oneTimeRef];
            }
        }),
        spawnPendingPaymentMachine: assign({
            currentPaymentFlow: (context) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const pendingRef = spawn(pendingPaymentMachine);
                return [paymentTypes.PENDING, pendingRef];
            }
        }),
        spawnAutopayMachine: assign({
            currentPaymentFlow: (context, event) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const { apiMachineRef, triggerAnalyticsEvent } = context;
                const autopayActorRef = spawn(autopayMachine.withContext({
                    apiMachineRef,
                    isPaymentTypeVisible: event.isPaymentTypeVisible,
                    triggerAnalyticsEvent
                }));
                return [paymentTypes.AUTOPAY, autopayActorRef];
            }
        }),
        spawnPayoffPaymentMachine: assign({
            currentPaymentFlow: (context) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const { apiMachineRef, featureFlags, triggerAnalyticsEvent } = context;
                const payoffRef = spawn(payoffPaymentMachine.withContext({
                    apiMachineRef,
                    featureFlags,
                    triggerAnalyticsEvent
                }));
                return [paymentTypes.PAYOFF, payoffRef];
            }
        }),
        triggerAnalyticsEvent: ({ triggerAnalyticsEvent }, event, meta) => {
            try {
                const { action: { eventOptions } } = meta;
                triggerAnalyticsEvent(eventOptions);
            }
            catch (_a) {
                // noop
            }
        }
    },
    guards: {
        hasAllDataFetchedSuccessfully: ({ apiMachineRef }) => {
            const { accountActivityQueryResult, accountSummaryQueryResult, autopayStatusQueryResult, paymentEnterpriseEligibilityQueryResult, paymentMethodsQueryResult } = apiMachineRef.getSnapshot().context;
            const productType = accountSummaryQueryResult.data.product.type;
            const allDataFetchedSuccessfully = (isSecuredInstallmentLoanProduct(productType) ||
                accountActivityQueryResult.data) &&
                accountSummaryQueryResult.data &&
                autopayStatusQueryResult.data &&
                paymentEnterpriseEligibilityQueryResult.data &&
                paymentMethodsQueryResult.data;
            return Boolean(allDataFetchedSuccessfully);
        },
        isDelinquentAccount: ({ apiMachineRef }) => {
            const { accountSummaryQueryResult } = apiMachineRef.getSnapshot().context;
            const { billing, product } = accountSummaryQueryResult.data || {};
            const { daysDelinquent } = billing;
            return getMaximumPaymentDays(daysDelinquent, product) === 0;
        },
        isOneTimePaymentNotEligible: ({ apiMachineRef, featureFlags }) => {
            if (!featureFlags.isSecuredInstallmentEnabled) {
                return false;
            }
            const { data: paymentEnterpriseEligibility } = apiMachineRef.getSnapshot().context
                .paymentEnterpriseEligibilityQueryResult;
            const oneTimePaymentEligibilityDetail = getOneTimePaymentEligibilityDetail(paymentEnterpriseEligibility, 'MAKE_A_PAYMENT');
            return (oneTimePaymentEligibilityDetail === null || oneTimePaymentEligibilityDetail === void 0 ? void 0 : oneTimePaymentEligibilityDetail.eligible) === false;
        }
    }
});
export default mainMachine;
